import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Info, Check, X } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Per-user permission matrix — binary (granted / denied) toggles.
 *
 * The admin explicitly picks which permissions this user has. Role defaults
 * are ignored: on save we push the FULL selected set as `permissions_grant`
 * AND every unchecked permission as `permissions_deny`, so the backend
 * formula `role ∪ grant \ deny` collapses to exactly the checked set.
 * Result: permissions are independent of role.
 */
export default function UserPermissionsDialog({ open, target, onOpenChange, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/permissions/catalog").then((r) => {
      setCatalog(r.data.catalog);
      setRoleDefaults(r.data.role_defaults || {});
    }).catch(() => setCatalog([]));
    setFilter("");
  }, [open, target?.id]);

  // Compute initial "checked" set from server data: role_default ∪ grant \ deny.
  // Only runs when catalog + target are ready — before that the set is empty.
  useEffect(() => {
    if (!catalog || !target) return;
    const base = new Set(roleDefaults[target.role] || []);
    (target.permissions_grant || []).forEach((c) => base.add(c));
    (target.permissions_deny || []).forEach((c) => base.delete(c));
    setChecked(base);
  }, [catalog, roleDefaults, target?.id, target?.role, target?.permissions_grant, target?.permissions_deny, target]);

  const toggle = (code) => {
    const next = new Set(checked);
    if (next.has(code)) next.delete(code); else next.add(code);
    setChecked(next);
  };

  const setGroup = (grp, value) => {
    const next = new Set(checked);
    grp.perms.forEach((p) => { if (value) next.add(p.code); else next.delete(p.code); });
    setChecked(next);
  };

  const save = async () => {
    if (!catalog) return;
    // Collect ALL known permission codes to build grant/deny arrays that
    // fully override the role default in effective_permissions().
    const allCodes = [];
    catalog.forEach((grp) => grp.perms.forEach((p) => allCodes.push(p.code)));
    const grant = Array.from(checked);
    const deny = allCodes.filter((c) => !checked.has(c));
    setBusy(true);
    try {
      await api.put(`/users/${target.id}/permissions`, {
        permissions_grant: grant,
        permissions_deny: deny,
      });
      toast.success("Permisos actualizados");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    if (!catalog) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return catalog;
    return catalog
      .map((grp) => ({ ...grp, perms: grp.perms.filter((p) => (p.code + " " + p.label).toLowerCase().includes(f)) }))
      .filter((grp) => grp.perms.length > 0);
  }, [catalog, filter]);

  if (!target) return null;

  const totalChecked = checked.size;
  const totalCatalog = catalog ? catalog.reduce((s, g) => s + g.perms.length, 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-blue-900" /> Permisos de {target.name}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-blue-50/70 border border-blue-200 rounded p-2.5 text-xs text-blue-900 flex items-start gap-2">
          <Info size={14} weight="bold" className="mt-0.5 shrink-0" />
          <div>
            Selecciona los permisos que tendrá <b>{target.name}</b>. Los permisos son <b>independientes del rol</b> —
            solo lo que marques aquí queda concedido. Actualmente: <b>{totalChecked}</b> de {totalCatalog} concedidos.
          </div>
        </div>

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrar permisos…"
          className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
          data-testid="perm-filter"
        />

        {catalog === null ? (
          <p className="text-sm text-slate-400 text-center py-6">Cargando catálogo…</p>
        ) : (
          <div className="space-y-4">
            {filtered.map((grp) => {
              const allOn = grp.perms.every((p) => checked.has(p.code));
              const anyOn = grp.perms.some((p) => checked.has(p.code));
              return (
                <div key={grp.group} className="border border-slate-200 rounded" data-testid={`perm-group-${grp.group}`}>
                  <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-3 py-2">
                    <p className="font-heading font-bold text-sm text-slate-800">
                      {grp.label}
                      <span className="ml-2 text-[10px] uppercase font-bold text-slate-400">
                        {grp.perms.filter((p) => checked.has(p.code)).length}/{grp.perms.length}
                      </span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setGroup(grp, true)}
                        disabled={allOn}
                        data-testid={`perm-group-all-${grp.group}`}
                        className="text-[10px] uppercase font-bold text-emerald-700 hover:text-emerald-900 disabled:opacity-40 disabled:cursor-default"
                      >
                        Todo
                      </button>
                      <span className="text-slate-300 text-[10px]">·</span>
                      <button
                        onClick={() => setGroup(grp, false)}
                        disabled={!anyOn}
                        data-testid={`perm-group-none-${grp.group}`}
                        className="text-[10px] uppercase font-bold text-red-700 hover:text-red-900 disabled:opacity-40 disabled:cursor-default"
                      >
                        Nada
                      </button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 p-3">
                    {grp.perms.map((p) => {
                      const on = checked.has(p.code);
                      return (
                        <button
                          key={p.code}
                          onClick={() => toggle(p.code)}
                          data-testid={`perm-toggle-${p.code}`}
                          className="flex items-center gap-2 py-1.5 pr-1 text-left hover:bg-slate-50 rounded transition group"
                          title={p.code}
                        >
                          <span className={`inline-flex items-center justify-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border shrink-0 min-w-[92px] text-center transition-colors ${
                            on ? "bg-emerald-500 text-white border-emerald-600" : "bg-red-500 text-white border-red-600"
                          }`}>
                            {on
                              ? (<><Check size={10} weight="bold" /> Concedido</>)
                              : (<><X size={10} weight="bold" /> Denegado</>)}
                          </span>
                          <span className="text-xs text-slate-700 leading-tight">{p.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancelar</Button>
          <Button onClick={save} disabled={busy} className="flex-1 bg-blue-900 hover:bg-blue-800" data-testid="perm-save">
            {busy ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
